import React from "react";
import PropTypes from "prop-types";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import CustomButton from "../CustomButton/CustomButton";
import { CustomText } from "../CustomText/CustomText";
import CustomSelect from "../CustomSelect/CustomSelect";

/**
 * Returns an array of page numbers and "…" ellipsis markers for a windowed
 * pagination strip. Always includes page 1 and the last page. For totals of
 * 3 or fewer pages every number is shown without ellipsis.
 *
 * @param {number} current  1-based current page
 * @param {number} total    total page count
 * @param {number} delta    number of pages to show either side of current (default 1)
 * @returns {(number|string)[]}
 */
function getPageRange(current, total, delta = 1) {
  if (total <= 3) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const left = current - delta;
  const right = current + delta;
  const inner = [];

  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= left && i <= right)) {
      inner.push(i);
    }
  }

  const range = [];
  let prev = null;
  for (const page of inner) {
    if (prev !== null && page - prev > 1) range.push("…");
    range.push(page);
    prev = page;
  }
  return range;
}

/**
 * Universal pagination component that handles both client-side and server-side pagination
 *
 * @param {Object} props
 * @param {string} props.mode - Pagination mode: "client" or "server"
 * @param {Object} props.pagination - Pagination state {pageIndex, pageSize}
 * @param {Function} props.setPagination - Function to update pagination state
 * @param {Array} props.pageSizeOptions - Array of page size options (default: [10, 20, 50])
 * @param {string} props.className - Additional CSS classes
 *
 * Server-side specific props:
 * @param {number} props.totalElements - Total number of elements from server
 * @param {number} props.totalPages - Total number of pages from server (optional)
 *
 * Client-side specific props:
 * @param {Object} props.table - TanStack table instance (for client-side)
 */
const Pagination = ({
  mode = "client",
  pagination,
  setPagination,
  pageSizeOptions = [10, 20, 50],
  className = "",
  // disable all controls (used during initial isLoading)
  disabled = false,
  // show page navigation buttons and page info (default: true)
  showNavigation = true,
  // show numbered page buttons (default: true)
  showPageNumbers = true,
  // debounce milliseconds for server-side pagination actions (0 = disabled)
  debounceMs = 300,
  // Server-side props
  totalElements,
  totalPages,
  // Client-side props
  table,
}) => {
  // Tracks the last non-zero pageCount so the numbered strip stays visible
  // during page transitions when the server response hasn't arrived yet.
  const lastPageCountRef = React.useRef(1);

  // Calculate pagination data based on mode
  const paginationData = React.useMemo(() => {
    if (mode === "server") {
      // Server-side pagination calculations
      const calculatedPages = Math.ceil(totalElements / pagination.pageSize);
      const rawPageCount = totalPages || calculatedPages;
      // Preserve last known count so the numbered strip doesn't disappear while fetching
      const pageCount = rawPageCount > 0 ? rawPageCount : lastPageCountRef.current;
      if (rawPageCount > 0) lastPageCountRef.current = rawPageCount;

      const currentPage = pagination.pageIndex + 1;
      const canPreviousPage = pagination.pageIndex > 0;
      const canNextPage = pagination.pageIndex < pageCount - 1;

      return {
        pageCount,
        currentPage,
        canPreviousPage,
        canNextPage,
        totalElements,
        showTotalItems: totalElements != null,
      };
    } else {
      // Client-side pagination using TanStack table
      const total = table.getFilteredRowModel().rows.length;
      return {
        pageCount: table.getPageCount(),
        currentPage: pagination.pageIndex + 1,
        canPreviousPage: table.getCanPreviousPage(),
        canNextPage: table.getCanNextPage(),
        totalElements: total,
        showTotalItems: true,
      };
    }
  }, [mode, pagination, totalElements, totalPages, table]);
  const pageRange = React.useMemo(
    () => getPageRange(paginationData.currentPage, paginationData.pageCount),
    [paginationData.currentPage, paginationData.pageCount],
  );

  // Debounce helpers (server-side only)
  const debounceTimerRef = React.useRef(null);
  const pendingUpdateRef = React.useRef(null);

  const scheduleServerUpdate = (updater) => {
    // If debounce is disabled, apply immediately
    if (!debounceMs || debounceMs <= 0) {
      setPagination(updater);
      return;
    }

    // Store pending updater and reset timer
    pendingUpdateRef.current = updater;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      // Apply the latest pending update
      const pending = pendingUpdateRef.current;
      if (typeof pending === "function") setPagination(pending);
      else setPagination((prev) => ({ ...prev, ...pending }));
      pendingUpdateRef.current = null;
      debounceTimerRef.current = null;
    }, debounceMs);
  };

  // Clean up any pending debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingUpdateRef.current = null;
    };
  }, []);

  // Navigation handlers
  const navigationHandlers = React.useMemo(() => {
    if (mode === "server") {
      return {
        handleFirstPage: () => {
          scheduleServerUpdate((prev) => ({ ...prev, pageIndex: 0 }));
        },
        handlePreviousPage: () => {
          if (paginationData.canPreviousPage) {
            scheduleServerUpdate((prev) => ({
              ...prev,
              pageIndex: prev.pageIndex - 1,
            }));
          }
        },
        handleNextPage: () => {
          if (paginationData.canNextPage) {
            scheduleServerUpdate((prev) => ({
              ...prev,
              pageIndex: prev.pageIndex + 1,
            }));
          }
        },
        handleLastPage: () => {
          scheduleServerUpdate((prev) => ({
            ...prev,
            pageIndex: paginationData.pageCount - 1,
          }));
        },
        handleGoToPage: (idx) => {
          scheduleServerUpdate((prev) => ({ ...prev, pageIndex: idx }));
        },
      };
    } else {
      return {
        handleFirstPage: () => table.setPageIndex(0),
        handlePreviousPage: () => table.previousPage(),
        handleNextPage: () => table.nextPage(),
        handleLastPage: () => table.setPageIndex(table.getPageCount() - 1),
        handleGoToPage: (idx) => table.setPageIndex(idx),
      };
    }
  }, [
    mode,
    setPagination,
    table,
    paginationData.canPreviousPage,
    paginationData.canNextPage,
    paginationData.pageCount,
    debounceMs,
  ]);

  const handlePageSizeChange = (newSize) => {
    if (mode === "server") {
      // Keep the same server pageIndex when changing page size.
      // The UI will still show the same visible page (currentPage),
      // but the backend will be requested with the same page index
      // so you get new data for the same logical page.
      scheduleServerUpdate((prev) => ({
        ...prev,
        pageSize: Number(newSize),
        // Preserve the current backend page index
        pageIndex: prev.pageIndex,
      }));
    } else {
      table.setPageSize(Number(newSize));
      setPagination((p) => ({
        ...p,
        pageSize: Number(newSize),
        pageIndex: 0,
      }));
    }
  };

  return (
    <div
      className={`flex items-center justify-between mt-3 px-1 sm:px-2 gap-2 sm:gap-4 ${className}`}
    >
      <CustomSelect
        size="xs"
        value={String(pagination.pageSize)}
        onChange={(val) => handlePageSizeChange(Number(val))}
        disabled={disabled}
        data={pageSizeOptions.map((size) => ({
          value: String(size),
          label: String(size),
        }))}
        className="w-[75px] sm:w-[90px]"
      />

      {/* Page info and navigation */}
      {showNavigation && (
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Mobile only: text indicator */}
          <CustomText size="xs" className="sm:hidden whitespace-nowrap">
            Page {paginationData.currentPage} of {paginationData.pageCount}
          </CustomText>

          {/* First page - mobile only (desktop uses numbered strip) */}
          <div className="sm:hidden">
            <CustomButton
              onClick={navigationHandlers.handleFirstPage}
              disabled={disabled || !paginationData.canPreviousPage}
              title="First page"
              size="xs"
              variant="primary"
            >
              ⏮
            </CustomButton>
          </div>

          <CustomButton
            onClick={navigationHandlers.handlePreviousPage}
            disabled={disabled || !paginationData.canPreviousPage}
            title="Previous page"
            size="xs"
            variant="primary"
          >
            <HiChevronLeft size={14} />
          </CustomButton>

          {/* Numbered page strip - desktop only */}
          {showPageNumbers && (
            <div className="hidden sm:flex items-center gap-0.5">
              {pageRange.map((entry, i) =>
                entry === "…" ? (
                  <span
                    key={`ellipsis-after-${pageRange[i - 1]}`}
                    className="px-1 text-xs text-gray-400 select-none"
                  >
                    …
                  </span>
                ) : (
                  <CustomButton
                    key={entry}
                    size="xs"
                    variant={
                      entry === paginationData.currentPage
                        ? "filled"
                        : "default"
                    }
                    onClick={() => navigationHandlers.handleGoToPage(entry - 1)}
                    disabled={disabled}
                  >
                    {entry}
                  </CustomButton>
                ),
              )}
            </div>
          )}

          <CustomButton
            onClick={navigationHandlers.handleNextPage}
            disabled={disabled || !paginationData.canNextPage}
            title="Next page"
            size="xs"
            variant="primary"
          >
            <HiChevronRight size={14} />
          </CustomButton>

          {/* Last page - mobile only (desktop uses numbered strip) */}
          <div className="sm:hidden">
            <CustomButton
              onClick={navigationHandlers.handleLastPage}
              disabled={disabled || !paginationData.canNextPage}
              title="Last page"
              size="xs"
              variant="primary"
            >
              ⏭
            </CustomButton>
          </div>

          {/* Total item count - desktop only */}
          {paginationData.showTotalItems && (
            <CustomText
              size="xs"
              className="hidden sm:block whitespace-nowrap text-gray-400"
            >
              ({paginationData.totalElements} items)
            </CustomText>
          )}
        </div>
      )}
    </div>
  );
};

// PropTypes defined for developer experience
Pagination.propTypes = {
  /** Pagination mode: "client" for TanStack table, "server" for API pagination */
  mode: PropTypes.oneOf(["client", "server"]).isRequired,
  /** Pagination state {pageIndex, pageSize} */
  pagination: PropTypes.shape({
    pageIndex: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
  /** Function to update pagination state */
  setPagination: PropTypes.func.isRequired,
  /** Array of page size options */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Disable all pagination controls (used during initial load) */
  disabled: PropTypes.bool,
  /** Show page navigation buttons and page info (default: true) */
  showNavigation: PropTypes.bool,
  /** Show numbered page buttons between Prev and Next (default: true) */
  showPageNumbers: PropTypes.bool,
  /** debounce time in ms for server-side pagination actions (0 = disabled) */
  debounceMs: PropTypes.number,

  // Server-side specific props
  /** Total number of elements from server (required for server mode) */
  totalElements: PropTypes.number,
  /** Total number of pages from server (optional for server mode) */
  totalPages: PropTypes.number,

  // Client-side specific props
  /** TanStack table instance (required for client mode) */
  table: PropTypes.object,
};

export default Pagination;
